/**
 * Product Personalizer SINGLE-ENGRAVE-TOGGLE — Shopify Block Script
 *
 * Variante: 1 cara, cliente elige TEXTO o DISEÑO (no ambos).
 * Color + Talla conectados con variants reales de Shopify.
 * Disponibilidad real desde Shopify (agotado / disponible).
 *
 * Oculta los selectores de variant del theme y maneja todo internamente.
 * Triple interceptor + modal preview con portal al body.
 */

(function() {
  'use strict';

  // JPEG no tiene transparencia: lo transparente se vuelve NEGRO al exportar. Antes de
  // exportar se calca el canvas sobre blanco (si ya era opaco, no cambia nada).
  // Bug real 05/08/2026: los fondos con transparencia del Tag de Mascota se guardaban oscuros.
  function __pzSobreBlanco(cv) {
    var c = document.createElement('canvas');
    c.width = cv.width; c.height = cv.height;
    var x = c.getContext('2d');
    x.fillStyle = '#ffffff';
    x.fillRect(0, 0, c.width, c.height);
    x.drawImage(cv, 0, 0);
    return c;
  }


  class ProductPersonalizerSingleEngraveToggle {
    constructor(el) {
      this.el = el;
      this.config = null;
      this.productForm = null;
      this._previewUrl = null;

      this.canvas = el.querySelector('[data-pzd-canvas]');
      this.ctx = this.canvas ? this.canvas.getContext('2d') : null;

      this._productImages = {};
      this._designImages = {};

      // Variants de Shopify (Color + Talla -> variantId, available)
      this.shopifyVariants = [];
      this.variantMap = {}; // "colorName|sizeLabel" -> { id, available }

      this.state = {
        color: null,
        size: null,
        design: null,
        font: null,
        text: '',
        mode: 'design', // 'design' | 'text'
        variantId: null,
      };

      this._fontsLoaded = {};
      this.previewModal = null;

      this._init();
    }

    _init() {
      try {
        this.config = JSON.parse(this.el.dataset.pzdConfig);
      } catch (e) {
        console.error('[PZS-TOGGLE] Invalid config:', e);
        return;
      }
      if (this.config.type !== 'single-engrave-toggle') return;

      // Variants de Shopify (expuestos por el liquid)
      try {
        this.shopifyVariants = JSON.parse(this.el.dataset.pzdVariants || '[]');
      } catch (e) {
        this.shopifyVariants = [];
      }
      // Disponibilidad global del producto (para productos simples sin variantes)
      this.productAvailable = this.el.dataset.pzdProductAvailable === 'true';
      this._buildVariantMap();

      // Estado inicial
      this.state.mode = this.config.defaultMode || 'design';

      var colors = this.config.colors || [];
      var firstColor = colors.find(function(c) { return c.available !== false; });
      this.state.color = firstColor ? firstColor.id : null;

      var sizes = this.config.sizes || [];
      var firstSize = sizes.find(function(s) { return s.available !== false && this._isVariantAvailable(firstColor && firstColor.name, s.label); }.bind(this));
      if (!firstSize) firstSize = sizes.find(function(s) { return s.available !== false; });
      this.state.size = firstSize ? firstSize.id : null;

      var designs = this.config.designs || [];
      var firstDesign = designs.find(function(d) { return d.available !== false; });
      this.state.design = firstDesign ? firstDesign.id : null;

      var fonts = this.config.fonts || [];
      this.state.font = fonts.length > 0 ? fonts[0].family : null;

      this._loadFonts();
      this._applyStyles();
      this._showSections();

      this._renderColors();
      this._renderSizes();
      this._renderDesigns();
      this._renderFontSelector();

      this._updateLabels();
      this._updateBadges();
      this._updateModeUI();

      this._bindEvents();
      this._linkProductForm();
      this._hideThemeVariantSelectors();
      this._installCartInterceptors();

      this._updateVariantId();
      this._drawCanvas();
      this._updateCharCount();
      this._applyFontToTextarea();
      this._checkGlobalStock();
    }

    // Si es producto simple, el sync está activo y el stock global es 0 → aviso persistente.
    _checkGlobalStock() {
      if (this.config.enableStockSync !== false && this._isSimpleProduct() && !this.productAvailable) {
        var box = this.el.querySelector('[data-pzd-msg="error"]');
        if (box) {
          box.textContent = 'Producto agotado temporalmente.';
          box.classList.add('is-visible');
        }
      }
    }

    // ============================================================
    // SHOPIFY VARIANTS
    // ============================================================
    _buildVariantMap() {
      var self = this;
      var colorOpt = (this.config.shopifyColorOption || 'Color').toLowerCase();
      var sizeOpt = (this.config.shopifySizeOption || 'Talla').toLowerCase();
      (this.shopifyVariants || []).forEach(function(v) {
        // v.options puede ser un array o v.option1/option2/option3 según el formato del liquid
        var color = null, size = null;
        if (v.option1 !== undefined) {
          // Formato simple: options posicionales según product.options order
          // Necesitamos los nombres de opciones para mapear
          var opts = self.shopifyOptions || [];
          [v.option1, v.option2, v.option3].forEach(function(val, idx) {
            if (!val || !opts[idx]) return;
            var optName = (opts[idx].name || '').toLowerCase();
            if (optName === colorOpt) color = val;
            if (optName === sizeOpt) size = val;
          });
        } else if (Array.isArray(v.options)) {
          v.options.forEach(function(o) {
            var optName = (o.name || '').toLowerCase();
            if (optName === colorOpt) color = o.value;
            if (optName === sizeOpt) size = o.value;
          });
        }
        if (color && size) {
          var key = color + '|' + size;
          self.variantMap[key] = { id: v.id, available: v.available !== false };
        } else if (color && !size) {
          // Producto con solo opción Color (sin Talla)
          var k2 = color + '|';
          self.variantMap[k2] = { id: v.id, available: v.available !== false };
        }
      });
    }

    // ¿Producto simple (sin variantes reales de color/talla, solo "Default Title")?
    _isSimpleProduct() {
      var opts = this.shopifyOptions || [];
      if (opts.length === 0) return true;
      if (opts.length === 1 && (opts[0].name || '').toLowerCase() === 'title') return true;
      return false;
    }

    _isVariantAvailable(colorName, sizeLabel) {
      // 1) Sync de stock desactivado en el admin → siempre disponible (vende sin control de stock).
      if (this.config.enableStockSync === false) return true;
      // 2) Producto simple (sin variantes) → usar el stock GLOBAL del producto.
      if (this._isSimpleProduct()) return this.productAvailable;
      // 3) Sync activo pero NO se mapeó ninguna variante (config incompleta o nombres que no coinciden).
      //    No bloqueamos la venta — el aviso de "mapeo incompleto" sale en el admin, no acá.
      if (Object.keys(this.variantMap).length === 0) return true;
      // 4) Sync activo y hay variantes mapeadas → respetar stock real.
      if (!colorName) return false;
      var key = colorName + '|' + (sizeLabel || '');
      var v = this.variantMap[key];
      return v ? v.available : false;
    }

    _getVariantId(colorName, sizeLabel) {
      if (!colorName) return null;
      var key = colorName + '|' + (sizeLabel || '');
      var v = this.variantMap[key];
      return v ? v.id : null;
    }

    _updateVariantId() {
      var color = this._getActiveColor();
      var size = this._getActiveSize();
      if (!color) { this.state.variantId = null; return; }
      var sizeLabel = size ? (size.label || size.id) : '';
      this.state.variantId = this._getVariantId(color.name, sizeLabel);
      // Actualizar también el input name="id" del form de Shopify
      if (this.productForm && this.state.variantId) {
        var idInput = this.productForm.querySelector('input[name="id"], select[name="id"]');
        if (idInput) idInput.value = this.state.variantId;
      }
      this._injectHiddenInputs();
    }

    _hideThemeVariantSelectors() {
      // Ocultar selectores de variantes del theme en TODA la página de producto.
      // Los selectores del theme suelen estar FUERA del form (widget que actualiza el form via JS),
      // así que buscamos por patrón name^="options" o por wrappers comunes.

      // 1) Buscar todos los inputs/selects de opciones del theme y ocultar su wrapper más cercano.
      var optionInputs = document.querySelectorAll(
        'input[name^="options"], input[name^="Options"], ' +
        'select[name^="options"], select[name^="Options"]'
      );
      optionInputs.forEach(function(input) {
        // Subir hasta el primer wrapper visualmente significativo.
        var wrapper = input.closest(
          'fieldset, variant-radios, variant-selects, ' +
          '.product-form__input, .product-form__option, .product-options, ' +
          '.variant-input-wrap, .swatch, [data-product-option]'
        );
        if (wrapper) wrapper.style.display = 'none';
      });

      // 2) Custom elements estándar y custom de themes específicos (por si los inputs están adentro o son JS-only).
      document.querySelectorAll(
        'variant-radios, variant-selects, ' +
        '.product-form__variants, .product-form__option-selectors, ' +
        '.product-info__variant-picker, [data-variant-selector], ' +
        '.vvp'  // theme Toto V.7 (Mistikal) - variant visual picker propio
      ).forEach(function(el) {
        if (el.querySelector('[name="id"]')) return; // no ocultar si tiene el input name=id
        el.style.display = 'none';
      });

      // 3) Selectores adicionales custom que se pueden definir desde el admin (escape hatch).
      var customSelectors = (this.config && this.config.hideThemeSelectors) || '';
      if (customSelectors) {
        customSelectors.split(',').forEach(function(sel) {
          var s = sel.trim();
          if (!s) return;
          try {
            document.querySelectorAll(s).forEach(function(el) {
              el.style.display = 'none';
            });
          } catch (e) {}
        });
      }
    }

    // ============================================================
    // STYLES
    // ============================================================
    _applyStyles() {
      var styles = this.config.styles || {};
      var s = this.el.style;
      if (styles.labelSize) s.setProperty('--pzd-label-size', styles.labelSize);
      if (styles.inputSize) s.setProperty('--pzd-input-size', styles.inputSize);
      if (styles.accentColor) s.setProperty('--pzd-accent', styles.accentColor);
      if (styles.labelBold !== false) s.setProperty('--pzd-label-weight', '700');
      var btnSize = this.config.previewButtonFontSize;
      if (btnSize) s.setProperty('--pzd-preview-btn-font-size', btnSize + 'px');
    }

    _showSections() {
      var fonts = this.config.fonts || [];
      this.el.querySelectorAll('[data-pzd-section="fonts"]').forEach(function(sec) {
        sec.style.display = fonts.length > 1 ? '' : 'none';
      });
    }

    // ============================================================
    // FONTS
    // ============================================================
    _loadFonts() {
      var self = this;
      var fonts = this.config.fonts || [];
      fonts.forEach(function(font) {
        if (!font.src || self._fontsLoaded[font.family]) return;
        try {
          var ff = new FontFace(font.family, 'url(' + font.src + ')');
          ff.load().then(function(loaded) {
            document.fonts.add(loaded);
            self._fontsLoaded[font.family] = true;
            self._drawCanvas();
            if (font.family === self.state.font) self._applyFontToTextarea();
          }).catch(function() {});
        } catch (e) {}
      });
    }

    _applyFontToTextarea() {
      var ta = this.el.querySelector('[data-pzd-textarea]');
      if (!ta) return;
      var font = this._getActiveFont();
      if (!font || !font.family) return;
      ta.style.fontFamily = '"' + font.family + '", monospace';
      ta.style.textTransform = font.uppercase !== false ? 'uppercase' : 'none';
      if (font.letterSpacing != null) ta.style.letterSpacing = font.letterSpacing + 'em';
    }

    // Remueve emojis (emoticons, símbolos, banderas, variation selectors, ZWJ).
    // Preserva ♥ (U+2665) porque es un carácter grabable, no un emoji a color.
    _stripEmojis(str) {
      if (!str) return str;
      return str.replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{2664}\u{2666}-\u{27BF}\u{2300}-\u{23FF}\u{FE00}-\u{FE0F}\u{1F1E6}-\u{1F1FF}\u{200D}\u{20E3}\u{2B00}-\u{2BFF}]/gu, '');
    }

    // ============================================================
    // LABELS
    // ============================================================
    _updateLabels() {
      var c = this.config;
      this._setText('[data-pzd-color-label]', c.colorLabel || 'Color', true);
      this._setText('[data-pzd-size-label]', c.sizeLabel || 'Talla', true);
      this._setText('[data-pzd-design-label]', c.designLabel || 'Diseño', true);
      this._setText('[data-pzd-text-label]', (c.text && c.text.sectionLabel) || 'Texto a grabar');
      this._setText('[data-pzd-font-label]', c.fontLabel || 'Tipografía');
      this._setText('[data-pzd-preview-label]', c.previewLabel || 'Vista previa');
      this._setText('[data-pzd-preview-hint]', c.previewHint || '');
      this._setText('[data-pzd-preview-btn-label]', c.previewButtonLabel || 'Ver vista previa');
      var ta = this.el.querySelector('[data-pzd-textarea]');
      if (ta && c.text && c.text.placeholder) ta.setAttribute('placeholder', c.text.placeholder);
    }

    _setText(selector, text, preserveChildren) {
      var el = this.el.querySelector(selector);
      if (!el) return;
      if (preserveChildren) {
        var child = el.firstChild;
        if (child && child.nodeType === 3) child.nodeValue = text + ' ';
        else el.insertBefore(document.createTextNode(text + ' '), el.firstChild);
      } else {
        el.textContent = text;
      }
    }

    _updateBadges() {
      var color = this._getActiveColor();
      if (color) this._setBadge('[data-pzd-color-name]', color.name);
      var size = this._getActiveSize();
      if (size) this._setBadge('[data-pzd-size-name]', size.label || size.id);
      var design = this._getActiveDesign();
      if (design) this._setBadge('[data-pzd-design-name]', design.name);
    }

    _setBadge(selector, text) {
      var el = this.el.querySelector(selector);
      if (el) el.textContent = text || '';
    }

    // ============================================================
    // GETTERS
    // ============================================================
    _getActiveColor() {
      return (this.config.colors || []).find((c) => c.id === this.state.color) || null;
    }
    _getActiveSize() {
      return (this.config.sizes || []).find((s) => s.id === this.state.size) || null;
    }
    _getActiveDesign() {
      return (this.config.designs || []).find((d) => d.id === this.state.design) || null;
    }
    _getActiveFont() {
      return (this.config.fonts || []).find((f) => f.family === this.state.font) || (this.config.fonts || [])[0] || null;
    }
    _getMaxChars() {
      var f = this._getActiveFont();
      return (f && f.maxChars) || 120;
    }
    _getTextArea() {
      var color = this._getActiveColor();
      if (color && color.areas && color.areas.text) return color.areas.text;
      var t = this.config.text || {};
      return t.area || { x: 0.10, y: 0.35, width: 0.80, height: 0.30 };
    }
    _getDesignArea() {
      var color = this._getActiveColor();
      if (color && color.areas && color.areas.design) return color.areas.design;
      return this.config.designArea || { x: 0.10, y: 0.35, width: 0.80, height: 0.30 };
    }
    _getTextColor() {
      var color = this._getActiveColor();
      return (color && color.textColor) || 'rgba(50, 45, 35, 0.88)';
    }

    // ============================================================
    // RENDER COLORS
    // ============================================================
    _renderColors() {
      var self = this;
      var container = this.el.querySelector('[data-pzd-colors]');
      if (!container) return;
      container.innerHTML = '';
      var size = this.config.colorSize || 60;
      // 🔴 Los colores con `available: false` en el metafield YA NO se quitan: se muestran
      // en gris como agotados, igual que los que se quedan sin stock en Shopify (pedido de
      // Toto, 05/08/2026 — mismo cambio en single-engrave, ver el comentario de allá).
      var colors = (this.config.colors || []).slice();

      // Un color se puede elegir solo si pasa LAS DOS señales: el metafield Y el stock real.
      var sePuede = function(c) { return c.available !== false && self._colorHasAnyAvailableSize(c); };

      // Disponibles primero, agotados al final (sort estable)
      colors = colors.slice().sort(function(a, b) {
        return (sePuede(a) ? 0 : 1) - (sePuede(b) ? 0 : 1);
      });

      // Solo se oculta el selector si el producto tiene UN color en total.
      var colorGroup = container.closest('.pzd__group');
      if (colorGroup) colorGroup.style.display = (colors.length <= 1) ? 'none' : '';

      colors.forEach(function(color) {
        var hasAvail = sePuede(color);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__color-btn' + (color.id === self.state.color ? ' is-active' : '') + (hasAvail ? '' : ' is-soldout');
        btn.style.setProperty('--pzd-color-size', size + 'px');
        btn.setAttribute('data-pzd-color-id', color.id);
        if (!hasAvail) btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-label', color.name + (hasAvail ? '' : ' (agotado)'));
        var inner = '<div class="pzd__color-inner" style="background:' + (color.hex || '#888') + ';';
        if (color.thumbnail) {
          inner += 'background-image:url(' + color.thumbnail + ');background-size:cover;background-position:center;';
        }
        inner += '"></div>';
        btn.innerHTML = inner;
        container.appendChild(btn);
      });

      // Si el color activo quedó agotado (todas sus tallas sin stock), mover al primer color elegible.
      var activeColor = this._getActiveColor();
      if (activeColor && !sePuede(activeColor)) {
        var firstAvail = colors.find(function(c) { return sePuede(c); });
        if (firstAvail && firstAvail.id !== this.state.color) {
          this.state.color = firstAvail.id;
          this.el.querySelectorAll('[data-pzd-color-id]').forEach(function(b) {
            b.classList.toggle('is-active', b.getAttribute('data-pzd-color-id') === firstAvail.id);
          });
        } else if (!firstAvail) {
          // 🔴 TODO agotado → NINGUNO queda seleccionado (pedido de Toto, 05/08/2026).
          // Mismo cambio que en single-engrave: un color activo en un producto agotado
          // parece invitar a comprarlo. El canvas muestra su placeholder sin color.
          this.state.color = null;
          this.el.querySelectorAll('[data-pzd-color-id]').forEach(function(b) {
            b.classList.remove('is-active');
          });
          var badgeColor = this.el.querySelector('[data-pzd-color-name]');
          if (badgeColor) badgeColor.textContent = '';
          this._drawCanvas();
        }
      }
    }

    _colorHasAnyAvailableSize(color) {
      var sizes = (this.config.sizes || []).filter(function(s) { return s.available !== false; });
      if (sizes.length === 0) {
        // Sin tallas configuradas: solo chequear si el color (solo Color, sin Talla) está disponible
        var v = this.variantMap[color.name + '|'];
        return v ? v.available : true;
      }
      for (var i = 0; i < sizes.length; i++) {
        if (this._isVariantAvailable(color.name, sizes[i].label || sizes[i].id)) return true;
      }
      return false;
    }

    _selectColor(colorId) {
      var color = (this.config.colors || []).find(function(c) { return c.id === colorId; });
      // Agotado por el metafield O sin stock en todas sus tallas: no seleccionable (ahora
      // los agotados sí se dibujan, así que este candado es el que evita elegirlos).
      if (color && (color.available === false || !this._colorHasAnyAvailableSize(color))) return;
      this.state.color = colorId;
      this.el.querySelectorAll('[data-pzd-color-id]').forEach(function(b) {
        b.classList.toggle('is-active', b.getAttribute('data-pzd-color-id') === colorId);
      });
      this._renderSizes(); // re-render para actualizar disponibilidad por talla con el nuevo color
      this._updateBadges();
      this._updateVariantId();
      this._drawCanvas();
    }

    // ============================================================
    // RENDER SIZES (chips)
    // ============================================================
    _renderSizes() {
      var self = this;
      var container = this.el.querySelector('[data-pzd-sizes]');
      if (!container) return;
      container.innerHTML = '';
      var sizes = (this.config.sizes || []).filter(function(s) { return s.available !== false; });
      if (sizes.length === 0) {
        // Ocultar la sección si no hay tallas
        var sec = this.el.querySelector('[data-pzd-section="sizes"]');
        if (sec) sec.style.display = 'none';
        return;
      }
      var sec = this.el.querySelector('[data-pzd-section="sizes"]');
      if (sec) sec.style.display = '';

      var size = this.config.sizeSize || 48;
      var shape = this.config.sizeShape || 'circle';
      var color = this._getActiveColor();

      // Tallas disponibles primero, agotadas al final (sort estable: respeta el orden 7-8-9...
      // dentro de cada grupo). La disponibilidad depende del color activo.
      sizes = sizes.slice().sort(function(a, b) {
        var aOk = color ? self._isVariantAvailable(color.name, a.label || a.id) : true;
        var bOk = color ? self._isVariantAvailable(color.name, b.label || b.id) : true;
        return (aOk ? 0 : 1) - (bOk ? 0 : 1);
      });

      sizes.forEach(function(sz) {
        var available = color ? self._isVariantAvailable(color.name, sz.label || sz.id) : false;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__size-btn pzd__size-btn--' + shape +
          (sz.id === self.state.size ? ' is-active' : '') +
          (available ? '' : ' is-soldout');
        btn.style.setProperty('--pzd-size-size', size + 'px');
        btn.setAttribute('data-pzd-size-id', sz.id);
        if (!available) btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('aria-label', (sz.label || sz.id) + (available ? '' : ' (agotado)'));
        btn.textContent = sz.label || sz.id;
        container.appendChild(btn);
      });

      // Si la talla activa no está disponible para este color, mover a la primera disponible
      var activeSize = this._getActiveSize();
      if (color && activeSize && !this._isVariantAvailable(color.name, activeSize.label || activeSize.id)) {
        var firstAvail = sizes.find(function(s) { return self._isVariantAvailable(color.name, s.label || s.id); });
        if (firstAvail) {
          this.state.size = firstAvail.id;
          this.el.querySelectorAll('[data-pzd-size-id]').forEach(function(b) {
            b.classList.toggle('is-active', b.getAttribute('data-pzd-size-id') === firstAvail.id);
          });
          this._updateBadges();
        }
      }
    }

    _selectSize(sizeId) {
      var color = this._getActiveColor();
      var size = (this.config.sizes || []).find(function(s) { return s.id === sizeId; });
      if (!size || !color) return;
      if (!this._isVariantAvailable(color.name, size.label || size.id)) return; // bloqueado, agotado
      this.state.size = sizeId;
      this.el.querySelectorAll('[data-pzd-size-id]').forEach(function(b) {
        b.classList.toggle('is-active', b.getAttribute('data-pzd-size-id') === sizeId);
      });
      this._updateBadges();
      this._updateVariantId();
    }

    // ============================================================
    // RENDER DESIGNS
    // ============================================================
    _renderDesigns() {
      var self = this;
      var container = this.el.querySelector('[data-pzd-designs]');
      if (!container) return;
      container.innerHTML = '';
      var size = this.config.designSize || 60;
      var designs = (this.config.designs || []).filter(function(d) { return d.available !== false; });
      if (designs.length === 0) return;

      designs.forEach(function(design) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'pzd__design-btn' + (design.id === self.state.design ? ' is-active' : '');
        btn.style.setProperty('--pzd-design-size', size + 'px');
        btn.setAttribute('data-pzd-design-id', design.id);
        btn.setAttribute('aria-label', design.name);
        if (design.thumbnail) {
          btn.innerHTML = '<img class="pzd__design-img" src="' + design.thumbnail + '" alt="' + design.name + '" />';
        } else {
          btn.textContent = design.name;
        }
        container.appendChild(btn);
      });
    }

    _selectDesign(designId) {
      this.state.design = designId;
      this.el.querySelectorAll('[data-pzd-design-id]').forEach(function(b) {
        b.classList.toggle('is-active', b.getAttribute('data-pzd-design-id') === designId);
      });
      this._updateBadges();
      this._drawCanvas();
      this._injectHiddenInputs();
    }

    // ============================================================
    // FONT SELECTOR
    // ============================================================
    _renderFontSelector() {
      var self = this;
      var fonts = this.config.fonts || [];
      if (fonts.length <= 1) return;
      var picker = this.el.querySelector('[data-pzd-font-picker]');
      if (!picker) return;
      var preview = picker.querySelector('[data-pzd-font-picker-preview]');
      var dropdown = picker.querySelector('[data-pzd-font-dropdown]');
      var activeFont = this._getActiveFont();
      if (preview && activeFont) {
        preview.textContent = activeFont.label || activeFont.family;
        preview.style.fontFamily = '"' + activeFont.family + '", sans-serif';
      }
      if (dropdown) {
        dropdown.innerHTML = '';
        fonts.forEach(function(font) {
          var opt = document.createElement('button');
          opt.type = 'button';
          opt.className = 'pzd__font-picker-option' + (font.family === self.state.font ? ' is-active' : '');
          opt.setAttribute('data-pzd-font-family', font.family);
          opt.style.fontFamily = '"' + font.family + '", sans-serif';
          opt.textContent = font.label || font.family;
          dropdown.appendChild(opt);
        });
      }
    }

    _selectFont(family) {
      this.state.font = family;
      var font = this._getActiveFont();
      var preview = this.el.querySelector('[data-pzd-font-picker-preview]');
      if (preview && font) {
        preview.textContent = font.label || font.family;
        preview.style.fontFamily = '"' + font.family + '", sans-serif';
      }
      this.el.querySelectorAll('[data-pzd-font-family]').forEach(function(o) {
        o.classList.toggle('is-active', o.getAttribute('data-pzd-font-family') === family);
      });
      var picker = this.el.querySelector('[data-pzd-font-picker]');
      if (picker) picker.classList.remove('is-open');
      var ta = this.el.querySelector('[data-pzd-textarea]');
      if (ta) {
        var max = this._getMaxChars();
        if (ta.value.length > max) ta.value = ta.value.substring(0, max);
        this.state.text = ta.value;
      }
      this._applyFontToTextarea();
      this._updateCharCount();
      this._drawCanvas();
      this._injectHiddenInputs();
    }

    // ============================================================
    // MODE TOGGLE (Diseño / Texto)
    // ============================================================
    _setMode(mode) {
      this.state.mode = mode;
      this._updateModeUI();
      this._drawCanvas();
      this._injectHiddenInputs();
    }

    _updateModeUI() {
      var mode = this.state.mode;
      // Buttons del toggle
      this.el.querySelectorAll('[data-pzd-mode]').forEach(function(b) {
        b.classList.toggle('is-active', b.getAttribute('data-pzd-mode') === mode);
      });
      // Contenido visible
      var designSec = this.el.querySelector('[data-pzd-mode-content="design"]');
      var textSec = this.el.querySelector('[data-pzd-mode-content="text"]');
      if (designSec) designSec.style.display = (mode === 'design') ? '' : 'none';
      if (textSec) textSec.style.display = (mode === 'text') ? '' : 'none';
    }

    // ============================================================
    // TEXT INPUT
    // ============================================================
    _updateCharCount() {
      var counter = this.el.querySelector('[data-pzd-char-count]');
      if (!counter) return;
      var max = this._getMaxChars();
      var remaining = max - this.state.text.length;
      counter.textContent = remaining + ' / ' + max;
      counter.classList.toggle('is-low', remaining < 10);
    }

    // ============================================================
    // CANVAS
    // ============================================================
    _drawCanvas() {
      if (!this.ctx) return;
      var self = this;
      var color = this._getActiveColor();
      if (!color || !color.image) {
        this._showCanvasPlaceholder('Selecciona un color con foto.');
        return;
      }
      this._loadProductImage(color, function(img) {
        if (!img) { self._showCanvasPlaceholder('No se pudo cargar la foto.'); return; }
        self._renderCanvas(img);
        self._schedulePreviewUpload();
      });
    }

    _loadProductImage(color, cb) {
      var self = this;
      if (this._productImages[color.id]) { cb(this._productImages[color.id]); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() { self._productImages[color.id] = img; cb(img); };
      img.onerror = function() { cb(null); };
      img.src = color.image;
    }

    _loadDesignImage(design, colorId, cb) {
      var self = this;
      if (!design) { cb(null); return; }
      var key = design.id + '_' + colorId;
      if (this._designImages[key]) { cb(this._designImages[key]); return; }
      var url = (design.images || {})[colorId] || design.thumbnail || '';
      if (!url) { cb(null); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function() { self._designImages[key] = img; cb(img); };
      img.onerror = function() { cb(null); };
      img.src = url;
    }

    _renderCanvas(productImg) {
      var self = this;
      var ctx = this.ctx;
      var w = productImg.naturalWidth;
      var h = productImg.naturalHeight;
      this.canvas.width = w;
      this.canvas.height = h;
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(productImg, 0, 0, w, h);

      if (this.state.mode === 'design') {
        var design = this._getActiveDesign();
        if (design) {
          this._loadDesignImage(design, this.state.color, function(designImg) {
            if (designImg) self._drawDesignInArea(designImg);
          });
        }
      } else {
        this._drawTextLayer();
      }
    }

    _drawDesignInArea(designImg) {
      var area = this._getDesignArea();
      var ctx = this.ctx;
      var w = this.canvas.width, h = this.canvas.height;
      var areaX = w * area.x, areaY = h * area.y;
      var areaW = w * area.width, areaH = h * area.height;
      var imgW = designImg.naturalWidth, imgH = designImg.naturalHeight;
      // Escala propia del diseño (1 = tamaño normal dentro del área). Permite achicar
      // diseños con mucho margen transparente (ej: herradura) o agrandar los que llenan poco.
      var design = this._getActiveDesign();
      var userScale = (design && typeof design.scale === 'number') ? design.scale : 1;
      var containScale = Math.min(areaW / imgW, areaH / imgH) * userScale;
      var drawW = imgW * containScale, drawH = imgH * containScale;
      var cx = areaX + (areaW - drawW) / 2, cy = areaY + (areaH - drawH) / 2;
      ctx.save();
      ctx.drawImage(designImg, cx, cy, drawW, drawH);
      ctx.restore();
    }

    _drawTextLayer() {
      var text = this.state.text;
      if (!text || !text.trim()) return;
      var area = this._getTextArea();
      var textCfg = this.config.text || {};
      var font = this._getActiveFont() || {};
      var family = font.family || 'monospace';
      var ctx = this.ctx;
      var w = this.canvas.width, h = this.canvas.height;
      var color = this._getTextColor();
      var shadowColor = textCfg.shadowColor || 'rgba(0, 0, 0, 0.3)';
      var sx = textCfg.shadowOffsetX != null ? textCfg.shadowOffsetX : 1;
      var sy = textCfg.shadowOffsetY != null ? textCfg.shadowOffsetY : 1;
      var areaX = w * area.x, areaY = h * area.y;
      var areaW = w * area.width, areaH = h * area.height;
      var maxPx = Math.round(w * (textCfg.maxFontSize || 0.07));
      var minPx = Math.round(w * (textCfg.minFontSize || 0.025));
      var rawText = font.uppercase !== false ? text.toUpperCase() : text;
      var rawLines = rawText.split('\n');
      var lineHeightRatio = font.lineHeight || 1.4;
      var fontSize = this._findFittingFontSize(ctx, rawLines, areaW, areaH, minPx, maxPx, family, lineHeightRatio);

      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = 'bold ' + fontSize + 'px "' + family + '", monospace';
      var wrappedLines = [];
      for (var i = 0; i < rawLines.length; i++) {
        var ln = rawLines[i];
        if (!ln.trim()) { wrappedLines.push(''); continue; }
        wrappedLines = wrappedLines.concat(this._wrapText(ctx, ln, areaW));
      }
      var totalH = wrappedLines.length * fontSize * lineHeightRatio;
      var startY = areaY + (areaH - totalH) / 2 + fontSize * lineHeightRatio / 2;
      // Offset vertical por fuente (en em, multiplicado por fontSize para convertir a px)
      startY += (font.yOffset || 0) * fontSize;
      var x = areaX + areaW / 2;
      for (var j = 0; j < wrappedLines.length; j++) {
        var txt = wrappedLines[j];
        if (!txt) continue;
        var y = startY + j * fontSize * lineHeightRatio;
        ctx.fillStyle = shadowColor;
        ctx.fillText(txt, x + sx, y + sy);
        ctx.fillStyle = color;
        ctx.fillText(txt, x, y);
      }
      ctx.restore();
    }

    _wrapText(ctx, text, maxWidth) {
      var words = text.split(' ');
      var lines = [], current = '';
      for (var i = 0; i < words.length; i++) {
        var test = current ? current + ' ' + words[i] : words[i];
        if (ctx.measureText(test).width > maxWidth && current) {
          if (ctx.measureText(words[i]).width > maxWidth) {
            lines.push(current);
            var chars = words[i], chunk = '';
            for (var c = 0; c < chars.length; c++) {
              var trial = chunk + chars[c];
              if (ctx.measureText(trial).width > maxWidth && chunk) { lines.push(chunk); chunk = chars[c]; }
              else chunk = trial;
            }
            current = chunk;
          } else { lines.push(current); current = words[i]; }
        } else current = test;
      }
      if (current) lines.push(current);
      return lines;
    }

    _findFittingFontSize(ctx, rawLines, areaW, areaH, minPx, maxPx, family, lhr) {
      if (rawLines.length === 0 || rawLines.every(function(l) { return !l.trim(); })) return maxPx;
      var lo = minPx, hi = maxPx, best = minPx;
      for (var iter = 0; iter < 20; iter++) {
        var mid = Math.round((lo + hi) / 2);
        if (mid === lo) break;
        ctx.font = 'bold ' + mid + 'px "' + family + '", monospace';
        var totalWrapped = 0, fits = true;
        for (var i = 0; i < rawLines.length; i++) {
          var ln = rawLines[i];
          if (!ln.trim()) { totalWrapped++; continue; }
          var wrapped = this._wrapText(ctx, ln, areaW);
          totalWrapped += wrapped.length;
          for (var w = 0; w < wrapped.length; w++) {
            if (ctx.measureText(wrapped[w]).width > areaW + 0.5) { fits = false; break; }
          }
          if (!fits) break;
        }
        var totalH = totalWrapped * mid * lhr;
        if (fits && totalH <= areaH) { best = mid; lo = mid; }
        else hi = mid;
      }
      return best;
    }

    _showCanvasPlaceholder(msg) {
      if (!this.ctx || !this.canvas) return;
      this.canvas.width = 600;
      this.canvas.height = 600;
      this.ctx.fillStyle = '#e8e5e0';
      this.ctx.fillRect(0, 0, 600, 600);
      this.ctx.fillStyle = '#999';
      this.ctx.font = '16px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.fillText(msg, 300, 300);
    }

    // ============================================================
    // PREVIEW MODAL (portal al body)
    // ============================================================
    _openPreviewModal() {
      if (!this.previewModal) {
        this.previewModal = this.el.querySelector('[data-pzd-preview-modal]');
        if (!this.previewModal) return;
        // Copiar las variables CSS (--pzd-*) del root al modal ANTES de moverlo al body.
        // Sin esto el modal pierde acceso a las variables heredadas y los colores
        // (--pzd-accent, --pzd-label-size, etc.) caen al fallback del CSS.
        var rootStyle = this.el.style;
        for (var i = 0; i < rootStyle.length; i++) {
          var prop = rootStyle[i];
          if (prop && prop.indexOf('--') === 0) {
            this.previewModal.style.setProperty(prop, rootStyle.getPropertyValue(prop));
          }
        }
        document.body.appendChild(this.previewModal);
      }
      var modal = this.previewModal;
      var srcCanvas = this.canvas;
      var modalCanvas = modal.querySelector('[data-pzd-preview-modal-canvas]');
      if (srcCanvas && modalCanvas) {
        modalCanvas.width = srcCanvas.width;
        modalCanvas.height = srcCanvas.height;
        modalCanvas.getContext('2d').drawImage(srcCanvas, 0, 0);
      }
      modal.style.display = '';
      document.body.style.overflow = 'hidden';
    }

    _closePreviewModal() {
      var modal = this.previewModal || this.el.querySelector('[data-pzd-preview-modal]');
      if (modal) modal.style.display = 'none';
      document.body.style.overflow = '';
    }

    // ============================================================
    // FORM + HIDDEN INPUTS
    // ============================================================
    _linkProductForm() {
      var formId = this.el.dataset.pzdFormId;
      if (formId) this.productForm = document.getElementById(formId);
      if (!this.productForm) this.productForm = document.querySelector('form[action="/cart/add"]');
      this._injectHiddenInputs();
    }

    _injectHiddenInputs() {
      var container = this.el.querySelector('[data-pzd-hidden-inputs]');
      if (!container) return;
      container.innerHTML = '';
      var labels = this.config.cartLabels || {};
      var color = this._getActiveColor();
      var size = this._getActiveSize();
      var design = this._getActiveDesign();
      var font = this._getActiveFont();
      var formId = this.productForm ? this.productForm.id : null;
      var inputs = [];

      if (color) inputs.push({ name: 'properties[' + (labels.color || this.config.colorLabel || 'Color') + ']', value: color.name });
      if (size) inputs.push({ name: 'properties[' + (labels.size || this.config.sizeLabel || 'Talla') + ']', value: size.label || size.id });
      if (this.state.mode === 'design' && design) {
        inputs.push({ name: 'properties[' + (labels.design || this.config.designLabel || 'Diseño') + ']', value: design.name });
      }
      if (this.state.mode === 'text') {
        if (font && (this.config.fonts || []).length > 1) {
          inputs.push({ name: 'properties[' + (labels.font || 'Tipografía') + ']', value: font.label || font.family });
        }
        if (this.state.text) inputs.push({ name: 'properties[' + (labels.text || 'Texto') + ']', value: this.state.text });
      }
      if (this._previewUrl) inputs.push({ name: 'properties[_Vista Previa]', value: this._previewUrl });

      // Código único para el botón "Ver mi diseño": al último input VISIBLE + un input _ref
      if (this._previewUrl) {
        var _cref = String(this._previewUrl).replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
        for (var _i = inputs.length - 1; _i >= 0; _i--) {
          if (inputs[_i].name.indexOf('properties[_') !== 0) { inputs[_i].value += ' (#' + _cref + ')'; break; }
        }
        inputs.push({ name: 'properties[_ref]', value: _cref });
      }

      inputs.forEach(function(inp) {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = inp.name;
        input.value = inp.value;
        if (formId) input.setAttribute('form', formId);
        container.appendChild(input);
      });
    }

    // ============================================================
    // PREVIEW UPLOAD
    // ============================================================
    _schedulePreviewUpload() {
      var self = this;
      if (this._uploadTimer) clearTimeout(this._uploadTimer);
      this._uploadTimer = setTimeout(function() { self._uploadPreview(); }, 1500);
    }

    _uploadPreview() {
      var self = this;
      var uploadUrl = this.el.dataset.pzdUploadUrl;
      var uploadKey = this.el.dataset.pzdUploadKey;
      if (!uploadUrl || !uploadKey || !this.canvas) return;
      // Solo subir si hay personalización completa
      if (this.state.mode === 'text' && (!this.state.text || !this.state.text.trim())) return;
      if (this.state.mode === 'design' && !this.state.design) return;
      try {
        var dataUrl = __pzSobreBlanco(this.canvas).toDataURL('image/jpeg', 0.85);
        fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Upload-Key': uploadKey },
          body: JSON.stringify({ image: dataUrl, variantId: this.state.variantId }),
        }).then(function(res) { return res.ok ? res.json() : null; })
          .then(function(data) {
            if (data && data.url) { self._previewUrl = data.url; self._injectHiddenInputs(); }
          }).catch(function() {});
      } catch (e) {}
    }

    // ============================================================
    // VALIDATION
    // ============================================================
    _validateBeforeAddToCart() {
      var errors = [];
      // Producto simple agotado (stock global) → bloquear todo con un solo mensaje claro.
      if (this.config.enableStockSync !== false && this._isSimpleProduct() && !this.productAvailable) {
        return ['Este producto está agotado.'];
      }
      if (!this.state.color) errors.push('Elegí un color.');
      if ((this.config.sizes || []).length > 0 && !this.state.size) errors.push('Elegí una talla.');
      // Verificar disponibilidad
      var color = this._getActiveColor();
      var size = this._getActiveSize();
      if (color && size && !this._isVariantAvailable(color.name, size.label || size.id)) {
        errors.push('Esta combinación está agotada. Elegí otra.');
      }
      if (this.state.mode === 'design' && !this.state.design) errors.push('Elegí un diseño.');
      if (this.state.mode === 'text' && (!this.state.text || !this.state.text.trim())) errors.push('Escribí el texto a grabar.');
      return errors;
    }

    _showError(msg) {
      var box = this.el.querySelector('[data-pzd-msg="error"]');
      if (!box) { alert(msg); return; }
      box.textContent = msg;
      box.classList.add('is-visible');
      try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
      clearTimeout(this._errTimer);
      this._errTimer = setTimeout(function() { box.classList.remove('is-visible'); }, 5000);
    }

    // ============================================================
    // CART INTERCEPTORS
    // ============================================================
    _installCartInterceptors() {
      var self = this;

      document.addEventListener('click', function(e) {
        var btn = e.target.closest('._rsi-buy-now-button');
        if (!btn) return;
        var errors = self._validateBeforeAddToCart();
        if (errors.length) {
          e.preventDefault(); e.stopImmediatePropagation();
          self._showError(errors[0]);
        } else {
          self._updateVariantId();
          self._injectHiddenInputs();
        }
      }, true);

      if (this.productForm) {
        this.productForm.addEventListener('submit', function(e) {
          var errors = self._validateBeforeAddToCart();
          if (errors.length) {
            e.preventDefault(); e.stopImmediatePropagation();
            self._showError(errors[0]); return;
          }
          self._updateVariantId();
          self._injectHiddenInputs();
        }, true);
      }

      var origFetch = window.fetch;
      window.fetch = function(input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (url.indexOf('/cart/add') !== -1) {
          var errors = self._validateBeforeAddToCart();
          if (errors.length) {
            self._showError(errors[0]);
            return Promise.reject(new Error('Personalización incompleta'));
          }
          self._injectAsBodyProperties(init);
        }
        return origFetch.apply(this, arguments);
      };

      var origOpen = XMLHttpRequest.prototype.open;
      var origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(method, url) {
        this._pzd_url = url;
        return origOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function(body) {
        if (this._pzd_url && this._pzd_url.indexOf('/cart/add') !== -1) {
          var errors = self._validateBeforeAddToCart();
          if (errors.length) { self._showError(errors[0]); return; }
          body = self._mergePropertiesIntoXhrBody(body);
          return origSend.call(this, body);
        }
        return origSend.apply(this, arguments);
      };
    }

    _collectProperties() {
      var labels = this.config.cartLabels || {};
      var color = this._getActiveColor();
      var size = this._getActiveSize();
      var design = this._getActiveDesign();
      var font = this._getActiveFont();
      var props = {};
      if (color) props[labels.color || this.config.colorLabel || 'Color'] = color.name;
      if (size) props[labels.size || this.config.sizeLabel || 'Talla'] = size.label || size.id;
      if (this.state.mode === 'design' && design) {
        props[labels.design || this.config.designLabel || 'Diseño'] = design.name;
      }
      if (this.state.mode === 'text') {
        if (font && (this.config.fonts || []).length > 1) props[labels.font || 'Tipografía'] = font.label || font.family;
        if (this.state.text) props[labels.text || 'Texto'] = this.state.text;
      }
      if (this._previewUrl) props['_Vista Previa'] = this._previewUrl;

      // Código único para el botón "Ver mi diseño" del carrito (derivado del preview).
      // Se pega a la ÚLTIMA propiedad visible → el botón queda al FINAL de la lista.
      if (this._previewUrl) {
        var _cref = String(this._previewUrl).replace(/[^a-zA-Z0-9]/g, '').slice(-4).toUpperCase();
        var _cvis = Object.keys(props).filter(function (k) { return k.charAt(0) !== '_'; });
        if (_cref && _cvis.length) {
          props[_cvis[_cvis.length - 1]] += ' (#' + _cref + ')';
          props['_ref'] = _cref;
        }
      }

      return props;
    }

    _injectAsBodyProperties(init) {
      if (!init || !init.body) return;
      var props = this._collectProperties();
      try {
        if (typeof init.body === 'string') {
          var parsed = JSON.parse(init.body);
          parsed.id = this.state.variantId || parsed.id;
          parsed.properties = Object.assign({}, parsed.properties || {}, props);
          init.body = JSON.stringify(parsed);
        } else if (init.body instanceof FormData) {
          if (this.state.variantId) init.body.set('id', this.state.variantId);
          Object.keys(props).forEach(function(k) {
            init.body.set('properties[' + k + ']', props[k]);
          });
        }
      } catch (e) {}
    }

    _mergePropertiesIntoXhrBody(body) {
      var props = this._collectProperties();
      if (typeof body === 'string') {
        try {
          var params = new URLSearchParams(body);
          if (this.state.variantId) params.set('id', String(this.state.variantId));
          Object.keys(props).forEach(function(k) { params.set('properties[' + k + ']', props[k]); });
          return params.toString();
        } catch (e) { return body; }
      }
      if (body instanceof FormData) {
        if (this.state.variantId) body.set('id', String(this.state.variantId));
        Object.keys(props).forEach(function(k) { body.set('properties[' + k + ']', props[k]); });
        return body;
      }
      return body;
    }

    // ============================================================
    // EVENTS
    // ============================================================
    _bindEvents() {
      var self = this;

      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pzd-color-id]');
        if (btn && btn.getAttribute('aria-disabled') !== 'true') self._selectColor(btn.getAttribute('data-pzd-color-id'));
      });

      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pzd-size-id]');
        if (btn && btn.getAttribute('aria-disabled') !== 'true') self._selectSize(btn.getAttribute('data-pzd-size-id'));
      });

      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pzd-design-id]');
        if (btn) self._selectDesign(btn.getAttribute('data-pzd-design-id'));
      });

      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pzd-mode]');
        if (btn) self._setMode(btn.getAttribute('data-pzd-mode'));
      });

      this.el.addEventListener('click', function(e) {
        var btn = e.target.closest('[data-pzd-font-picker-btn]');
        if (btn) {
          var picker = btn.closest('[data-pzd-font-picker]');
          if (picker) picker.classList.toggle('is-open');
          return;
        }
        var opt = e.target.closest('[data-pzd-font-family]');
        if (opt) self._selectFont(opt.getAttribute('data-pzd-font-family'));
      });

      document.addEventListener('click', function(e) {
        if (!e.target.closest('[data-pzd-font-picker]')) {
          self.el.querySelectorAll('[data-pzd-font-picker]').forEach(function(p) { p.classList.remove('is-open'); });
        }
      });

      this.el.addEventListener('input', function(e) {
        if (e.target.matches('[data-pzd-textarea]')) {
          // Bloquear emojis si el admin los prohibió
          if (self.config.allowEmojis === false) {
            var cleaned = self._stripEmojis(e.target.value);
            if (cleaned !== e.target.value) e.target.value = cleaned;
          }
          var max = self._getMaxChars();
          if (e.target.value.length > max) e.target.value = e.target.value.substring(0, max);
          self.state.text = e.target.value;
          self._updateCharCount();
          self._drawCanvas();
          self._injectHiddenInputs();
        }
      });

      this.el.addEventListener('click', function(e) {
        if (e.target.closest('[data-pzd-open-preview]')) self._openPreviewModal();
      });

      document.addEventListener('click', function(e) {
        if (e.target.closest('[data-pzd-preview-modal-close]')) {
          var modal = e.target.closest('[data-pzd-preview-modal]');
          if (modal && modal === self.previewModal) self._closePreviewModal();
        }
      });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-product-personalizer-single-engrave-toggle]').forEach(function(el) {
      if (el._pzsInstance) return;
      // Pasar options de Shopify para mapeo de variants
      try {
        el._pzsInstance = new ProductPersonalizerSingleEngraveToggle(el);
        el._pzsInstance.shopifyOptions = JSON.parse(el.dataset.pzdOptions || '[]');
        el._pzsInstance._buildVariantMap();
        el._pzsInstance._renderColors();
        el._pzsInstance._renderSizes();
        el._pzsInstance._updateVariantId();
      } catch (e) { console.error('[PZS-TOGGLE] init error:', e); }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();
